'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface TimelinePhase {
  week: string;
  title: string;
  description: string;
  activities: string[];
  icon: string;
}

interface CurriculumTimelineProps {
  className?: string;
}

const CurriculumTimeline = ({ className = '' }: CurriculumTimelineProps) => {
  const [activePhase, setActivePhase] = useState<number>(0);

  const phases: TimelinePhase[] = [
    {
      week: 'Weeks 1-4',
      title: 'Grounding in Food Systems & Justice',
      description: 'Understanding food systems, inequity, biology, and infrastructure',
      activities: [
        'Program launch, community norms, intro to food systems & food deserts',
        'Food supply chains, access, and points of failure',
        'Plant biology and real biological constraints',
        'Synthesis activity, discussion, and systems-thinking reflection',
      ],
      icon: 'LightBulbIcon',
    },
    {
      week: 'Weeks 5-7',
      title: 'Computers as Systems',
      description: 'Understanding computers, data, logic, and computation',
      activities: [
        'Binary, data representation, and digital logic',
        'Defining computer science beyond "coding"',
        'Hands-on decoding/encoding activities',
        'Practice applying computational thinking to food/ag problems',
      ],
      icon: 'ComputerDesktopIcon',
    },
    {
      week: 'Weeks 8-10',
      title: 'Programming Foundations',
      description: 'Learning to think and write in code',
      activities: [
        'First C programs and program structure',
        'Variables, conditionals, loops',
        'Debugging as a mindset',
        'Extra time for practice, repetition, and confidence building',
      ],
      icon: 'CodeBracketIcon',
    },
    {
      week: 'Weeks 11-17',
      title: 'Embedded Systems & Hardware Control',
      description: 'Connecting code to the physical world',
      activities: [
        'Transition from C to Arduino',
        'Circuits, breadboards, sensors, and outputs',
        'Event-based programming and component communication',
        'Safely controlling high-power devices with extended build time',
      ],
      icon: 'WrenchScrewdriverIcon',
    },
    {
      week: 'Weeks 18-21',
      title: 'Intelligent & Connected Systems',
      description: 'Systems that respond, adapt, and connect',
      activities: [
        'Feedback loops and multi-stage logic',
        'Sensor-driven automation',
        'Introduction to IoT concepts',
        'Building and deploying a basic connected system with Arduino Cloud',
      ],
      icon: 'SignalIcon',
    },
    {
      week: 'Weeks 22-28',
      title: 'Capstone - Designing Smart Agricultural Systems',
      description: 'Synthesis, creativity, and real-world application',
      activities: [
        'System design, requirements, planning, pseudocode',
        'Build, code, test, debug, iterate',
        'Final integration, storytelling, presentation prep',
        'Public showcase, demonstrations, reflection',
      ],
      icon: 'SparklesIcon',
    },
  ];

  return (
    <section className={`py-20 bg-surface ${className}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold text-text-primary mb-4">
            28-Week Program Timeline
          </h2>
          <p className="text-lg text-text-secondary max-w-2xl mx-auto">
            A comprehensive journey from food systems understanding to designing smart agricultural systems grounded in food justice, biology, and engineering
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {phases.map((phase, index) => (
            <button
              key={index}
              onClick={() => setActivePhase(index)}
              className={`p-6 rounded-xl border-2 text-left transition-all duration-300 ${
                activePhase === index
                  ? 'border-brand-primary bg-brand-primary/5 shadow-brand-md'
                  : 'border-border bg-card hover:border-brand-primary/50 hover:shadow-brand-sm'
              }`}
            >
              <div className="flex items-center gap-3 mb-3">
                <div
                  className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    activePhase === index
                      ? 'bg-brand-primary text-brand-primary-foreground'
                      : 'bg-muted text-text-secondary'
                  }`}
                >
                  <Icon name={phase.icon as any} size={20} />
                </div>
                <span className="text-sm font-semibold text-text-secondary">{phase.week}</span>
              </div>
              <h3 className="text-lg font-bold text-text-primary mb-2">{phase.title}</h3>
              <p className="text-sm text-text-secondary">{phase.description}</p>
            </button>
          ))}
        </div>

        <div className="bg-card rounded-2xl p-8 border border-border shadow-brand-md">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-12 h-12 bg-brand-primary rounded-lg flex items-center justify-center">
              <Icon name={phases[activePhase].icon as any} size={24} className="text-brand-primary-foreground" />
            </div>
            <div>
              <div className="text-sm font-semibold text-text-secondary">{phases[activePhase].week}</div>
              <h3 className="text-2xl font-bold text-text-primary">{phases[activePhase].title}</h3>
            </div>
          </div>

          <p className="text-lg text-text-secondary mb-6">{phases[activePhase].description}</p>

          <div className="space-y-3">
            <h4 className="text-sm font-semibold text-text-primary uppercase tracking-wide">Key Activities</h4>
            {phases[activePhase].activities.map((activity, index) => (
              <div key={index} className="flex items-start gap-3">
                <div className="flex-shrink-0 w-6 h-6 bg-success/10 rounded-full flex items-center justify-center mt-0.5">
                  <Icon name="CheckIcon" size={14} className="text-success" />
                </div>
                <p className="text-text-secondary">{activity}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default CurriculumTimeline;